import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { Upload, CheckCircle } from 'lucide-react';

interface FormData {
  title: string;
  author: string;
  document_url?: string;
}

export function SymposiumPresentationUploadPage() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [submissionSuccessful, setSubmissionSuccessful] = useState(false);
  const [submittedData, setSubmittedData] = useState<FormData & { fileName?: string } | null>(null);
  const [formData, setFormData] = useState<FormData>({
    title: '',
    author: ''
  });

  // Inicijalizacija bucketa pri učitavanju komponente
  useEffect(() => {
    async function initializeStorage() {
      try {
        setIsInitializing(true);
        console.log('Započinjem inicijalizaciju storage-a...');
        
        // Prvo provjeri postoji li bucket
        const { data: buckets, error: bucketsError } = await supabase
          .storage
          .listBuckets();
        
        if (bucketsError) {
          console.error('Error getting buckets:', bucketsError);
          toast.error('Greška pri dohvaćanju bucketa.');
          setIsInitializing(false);
          return;
        }
        
        console.log('Dostupni bucketi:', buckets?.map(b => b.name) || []);
        const symposiumBucketExists = buckets?.some(bucket => bucket.name === 'symposium_presentations');
        
        // Ako bucket ne postoji, pokušaj ga kreirati
        if (!symposiumBucketExists) {
          console.log('Creating symposium_presentations bucket...');
          // Kreiraj bucket ako ne postoji
          const { data, error } = await supabase
            .storage
            .createBucket('symposium_presentations', {
              public: true, 
              fileSizeLimit: 26214400, // 25MB u bajtovima
              allowedMimeTypes: ['application/pdf', 'application/vnd.openxmlformats-officedocument.presentationml.presentation']
            });
          
          if (error) {
            console.error('Error creating bucket:', error);
            // Ako je greška zbog RLS pravila, nastavite s radom - bucket vjerojatno već postoji ali korisnik nema pravo vidjeti/kreirati
            if (error.message.includes('row-level security')) {
              console.log('RLS error detected when creating bucket. Continuing with existing bucket.');
              // Nastavi dalje - ne prikazujemo error korisniku jer ga to ne treba brinuti
            } else {
              toast.error('Greška pri inicijalizaciji sustava. Molimo pokušajte kasnije.');
            }
          } else {
            console.log('Bucket successfully created');
          }
        } else {
          console.log('Bucket symposium_presentations already exists');
        }
        
        // Provjera pristupa bucketu testnim uploadom umjesto getBucket
        try {
          console.log('Checking bucket access...');
          // Pokušaj pristupiti bucketovom root direktoriju
          const { data: listData, error: listError } = await supabase
            .storage
            .from('symposium_presentations')
            .list();
            
          if (listError) {
            console.error('Error listing bucket contents:', listError);
            console.log('This is expected if you don\'t have admin rights - upload might still work');
          } else {
            console.log('Successfully accessed bucket, found items:', listData.length);
          }
        } catch (accessErr) {
          console.error('Error accessing bucket:', accessErr);
        }
      } catch (err) {
        console.error('Error during storage initialization:', err);
        toast.error('Greška pri inicijalizaciji sustava. Molimo pokušajte kasnije.');
      } finally {
        console.log('Inicijalizacija storage-a završena');
        setIsInitializing(false);
      }
    }
    
    initializeStorage();
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.size > 25 * 1024 * 1024) { // 25MB limit
        toast.error('Datoteka je prevelika. Maksimalna veličina je 25MB.');
        return;
      }
      
      // Provjera formata datoteke
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      if (fileExt !== 'pdf' && fileExt !== 'pptx') {
        toast.error('Dozvoljeni su samo .pdf i .pptx formati.');
        return;
      }
      
      setSelectedFile(file);
    }
  };

  const uploadFile = async (file: File): Promise<string> => {
    try {
      console.log('======= UPLOAD FILE STARTED =======');
      // Osiguravanje jedinstvenog imena datoteke
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      const fileName = `symposium_presentation_${Date.now()}_${Math.floor(Math.random() * 1000)}.${fileExt}`;
      
      console.log(`Priprema uploada datoteke: ${fileName} (${file.size} bytes)`);
      console.log(`Tip datoteke: ${file.type}, Ekstenzija: ${fileExt}`);
      console.log('Target bucket: symposium_presentations');
      
      let contentType = 'application/octet-stream';
      if (fileExt === 'pdf') {
        contentType = 'application/pdf';
      } else if (fileExt === 'pptx') {
        contentType = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
      }
      
      console.log(`Postavljam Content-Type: ${contentType}`);
      
      // Umjesto provjere buckets s getBucket (što može izazvati RLS probleme),
      // idemo direktno pokušati uploadati
      
      // Priprema parametara za upload
      console.log('Priprema parametara za upload...');
      const uploadOptions = {
        contentType: contentType,
        cacheControl: '3600',
        upsert: false,
      };
      
      console.log('Upload options:', uploadOptions);
      
      // Pokušaj uploada datoteke s definiranim tipom sadržaja
      console.log('Započinjem upload datoteke...');
      const { data, error } = await supabase.storage
        .from('symposium_presentations')
        .upload(fileName, file, uploadOptions);

      if (error) {
        console.error('Storage upload error:', error);
        console.error('Error status:', (error as any).status);
        console.error('Error message:', error.message);
        console.error('Error name:', error.name);
        
        // Detaljnija analiza greške
        if (error.message.includes('row-level security') || error.message.includes('RLS')) {
          console.error('RLS policy error detected');
          toast.error('Greška s dozvolama za upload (RLS). Molimo kontaktirajte administratora.');
        } else if (error.message.includes('bucket') && error.message.includes('not found')) {
          console.error('Bucket not found error detected');
          toast.error('Bucket nije pronađen. Sustav će pokušati inicijalizirati storage ponovo.');
          
          // Pokušaj ponovne inicijalizacije bucketa
          const { error: createError } = await supabase
            .storage
            .createBucket('symposium_presentations', {
              public: true,
              fileSizeLimit: 26214400, // 25MB
            });
            
          if (createError) {
            console.error('Error recreating bucket:', createError);
          } else {
            console.log('Bucket successfully recreated, attempting upload again');
            // Ponovni pokušaj uploada nakon stvaranja bucketa
            return uploadFile(file);
          }
        } else if (error.message.includes('permission')) {
          console.error('Permission error detected');
          toast.error('Nemate dozvolu za upload datoteka. Molimo kontaktirajte administratora.');
        } else {
          console.error('Unknown upload error');
          toast.error(`Greška pri uploadu: ${error.message}`);
        }
        
        throw new Error(`Upload error: ${error.message}`);
      }
      
      console.log('Upload uspješan, dobiveni podaci:', data);
      
      // Dohvaćanje javnog URL-a
      console.log('Dohvaćam javni URL datoteke...');
      const { data: urlData } = supabase.storage
        .from('symposium_presentations')
        .getPublicUrl(fileName);
      
      if (!urlData || !urlData.publicUrl) {
        console.error('Failed to get public URL');
        throw new Error('Nije moguće dobiti javni URL za datoteku');
      }

      console.log('Dobiven javni URL:', urlData.publicUrl);
      console.log('======= UPLOAD FILE COMPLETED =======');
      return urlData.publicUrl;
    } catch (error: any) {
      console.error('======= UPLOAD FILE FAILED =======');
      console.error('Error in uploadFile function:', error);
      throw error;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedFile) {
      toast.error('Molimo odaberite datoteku za upload');
      return;
    }

    if (!formData.title.trim() || !formData.author.trim()) {
      toast.error('Molimo popunite naslov i autora prezentacije');
      return;
    }

    setIsSubmitting(true);

    try {
      console.log('Uploading file:', selectedFile.name, 'Size:', selectedFile.size);
      let documentUrl = await uploadFile(selectedFile);
      console.log('File uploaded successfully, URL:', documentUrl);
      
      // Spremanje podataka u bazu
      const { data, error } = await supabase
        .from('symposium_presentations')
        .insert([{
          title: formData.title.trim(),
          author: formData.author.trim(),
          document_url: documentUrl,
          type: 'symposium'
        }]);

      if (error) {
        console.error('Database insert error:', error);
        throw error;
      }

      toast.success('Prezentacija je uspješno uploadana!');
      
      // Spremi podatke za prikaz potvrde
      setSubmittedData({
        ...formData, 
        document_url: documentUrl,
        fileName: selectedFile.name
      });
      setSubmissionSuccessful(true);
    } catch (error: any) {
      console.error('Error during submission:', error);
      toast.error(`Došlo je do greške prilikom uploada: ${error.message || 'Molimo pokušajte ponovno'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Komponenta za prikaz potvrde uploada
  const UploadConfirmation = () => {
    if (!submittedData) return null;
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-white rounded-2xl shadow-xl overflow-hidden"
      >
        <div className="bg-gradient-to-r from-purple-600 to-purple-800 text-white p-8">
          <div className="flex items-center justify-center mb-4">
            <CheckCircle className="w-16 h-16 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-center">
            Prezentacija uspješno uploadana!
          </h1>
          <p className="text-purple-100 text-center mt-2">
            Hvala vam na vašem doprinosu simpoziju "Multidisciplinarni pristup u liječenju tumora probavnih organa"
          </p>
        </div>

        <div className="p-8">
          <div className="mb-8 bg-purple-50 p-6 rounded-lg border border-purple-200">
            <p className="text-gray-800">
              Vaša prezentacija je uspješno uploadana. Svi podaci su pohranjeni u našoj bazi podataka.
            </p>
          </div>

          <h2 className="text-2xl font-bold text-gray-800 mb-6">Pregled uploadanih podataka</h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Podaci o prezentaciji</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Naslov prezentacije</p>
                    <p className="text-gray-900 font-medium">{submittedData.title}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Autor</p>
                    <p className="text-gray-900 font-medium">{submittedData.author}</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Uploadana datoteka</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Naziv datoteke</p>
                    <p className="text-gray-900 font-medium">{submittedData.fileName}</p>
                  </div>
                  {submittedData.document_url && (
                    <div>
                      <p className="text-sm text-gray-500">Link za pregled</p>
                      <a 
                        href={submittedData.document_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-purple-600 hover:text-purple-800 underline"
                      >
                        Pregledaj prezentaciju
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => {
                setSubmissionSuccessful(false);
                setFormData({
                  title: '',
                  author: ''
                });
                setSelectedFile(null);
              }}
              className="px-6 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors"
            >
              Uploadaj novu prezentaciju
            </button>
            <button
              onClick={() => navigate('/simpozij')}
              className="px-6 py-3 bg-gray-200 text-gray-800 rounded-lg font-medium hover:bg-gray-300 transition-colors"
            >
              Povratak na početnu stranicu
            </button>
          </div>
        </div>
      </motion.div>
    );
  };

  if (isInitializing) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-12 flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-gray-700">Inicijalizacija sustava, molimo pričekajte...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-3xl mx-auto px-4"
      >
        {submissionSuccessful ? (
          <UploadConfirmation />
        ) : (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-purple-800 text-white p-8">
              <h1 className="text-3xl font-bold text-center">Upload prezentacije za simpozij</h1>
              <p className="text-purple-100 text-center mt-2">
                "Multidisciplinarni pristup u liječenju tumora probavnih organa"
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-8 space-y-8">
              {/* Title */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="space-y-4"
              >
                <h2 className="text-xl font-semibold text-gray-900">Naslov prezentacije</h2>
                <div>
                  <input
                    type="text"
                    name="title"
                    required
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Unesite naslov prezentacije"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </motion.div>

              {/* Author */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                className="space-y-4"
              >
                <h2 className="text-xl font-semibold text-gray-900">Autor</h2>
                <div>
                  <input
                    type="text"
                    name="author"
                    required
                    value={formData.author}
                    onChange={handleInputChange}
                    placeholder="Unesite ime i prezime autora"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </motion.div>

              {/* File Upload */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                className="space-y-4"
              >
                <h2 className="text-xl font-semibold text-gray-900">Upload dokumenta</h2>
                <div className="flex flex-col items-center justify-center w-full">
                  <label
                    htmlFor="file-upload"
                    className="w-full flex flex-col items-center justify-center px-4 py-6 border-2 border-purple-300 border-dashed rounded-lg cursor-pointer bg-purple-50 hover:bg-purple-100 transition-colors duration-200"
                  >
                    <Upload className="w-12 h-12 text-purple-500 mb-3" />
                    <div className="flex flex-col items-center">
                      <p className="mb-2 text-sm text-purple-500">
                        <span className="font-semibold">Kliknite za upload</span> ili povucite i ispustite
                      </p>
                      <p className="text-xs text-gray-500">
                        PPTX ili PDF (max. 25MB)
                      </p>
                    </div>
                    {selectedFile && (
                      <div className="mt-4 text-sm text-purple-600">
                        Odabrana datoteka: {selectedFile.name}
                      </div>
                    )}
                    <input
                      id="file-upload"
                      name="file-upload"
                      type="file"
                      className="hidden"
                      accept=".pptx,.pdf"
                      onChange={handleFileChange}
                    />
                  </label>
                </div>
              </motion.div>

              {/* Submit Button */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="pt-6"
              >
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full py-4 px-8 text-lg font-bold text-white bg-purple-600 rounded-full 
                    ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-purple-700'} 
                    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 
                    transition-colors duration-200`}
                >
                  {isSubmitting ? 'Uploadanje prezentacije...' : 'Upload prezentacije'}
                </button>
              </motion.div>
            </form>
          </div>
        )}
      </motion.div>
    </div>
  );
}